// GET remains usable when JavaScript or dialog support is unavailable.
export function filterUrl(action, entries) {
    const url = new URL(action);
    url.search = '';
    for (const [name, value] of entries) {
        if (value !== '' && name !== 'page' && !(name === 'orden' && value === 'recientes')) url.searchParams.append(name, value);
    }
    return url;
}

// The bare home introduces page-level hero CSS and the assistant registration.
// A full GET loads those dependencies before rendering the hero.
export function requiresShopDocument(url) {
    if (!/^\/(es|en|fr|ca)\/shop\/?$/.test(url.pathname)) return false;
    return [...url.searchParams].every(([name, value]) =>
        value === '' || (name === 'orden' && value === 'recientes') || (name === 'page' && value === '1'));
}

// Small transport boundary lets slow, failed and superseded requests be tested without a browser.
export function createFilterRequest({fetcher = globalThis.fetch, setTimer = setTimeout, clearTimer = clearTimeout, slowMs = 3000, timeoutMs = 20000} = {}) {
    let active;
    const cancel = () => {
        if (!active) return;
        active.cancelled = true;
        active.controller.abort();
        active.reject(new Error('cancelled'));
        active = undefined;
    };
    return {
        cancel,
        async run(url, state) {
            cancel();
            const current = {controller:new AbortController(), cancelled:false};
            const interrupted = new Promise((_, reject) => { current.reject = reject; });
            active = current;
            state('loading');
            const slow = setTimer(() => { if (active === current) state('slow'); }, slowMs);
            const deadline = setTimer(() => {
                current.controller.abort();
                current.reject(new Error('timeout'));
            }, timeoutMs);
            try {
                const data = await Promise.race([
                    (async () => {
                        const response = await fetcher(url, {
                            signal:current.controller.signal, credentials:'same-origin', cache:'no-store',
                            headers:{'X-Requested-With':'XMLHttpRequest', 'X-Shop-Filters':'1', 'Accept':'application/json'},
                        });
                        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) throw new Error('response');
                        return response.json();
                    })(),
                    interrupted,
                ]);
                if (current.cancelled || active !== current) return null;
                if (!data || typeof data.html !== 'string' || typeof data.canonical !== 'string') throw new Error('payload');
                const canonical = new URL(data.canonical, url);
                const target = new URL(url);
                if (canonical.origin !== target.origin || canonical.pathname !== target.pathname) throw new Error('scope');
                return data;
            } catch (_) {
                if (!current.cancelled && active === current) state('error');
                return null;
            } finally {
                clearTimer(slow);
                clearTimer(deadline);
                if (active === current) active = undefined;
            }
        },
    };
}

const owners = new WeakMap();
export function initializeShopFilters(doc = document) {
    const panel = doc.querySelector('[data-almc-filter-panel]');
    if (!panel || panel.dataset.enhanced) return;
    const win = doc.defaultView;
    const dialog = doc.createElement('dialog');
    if (!win || typeof dialog.showModal !== 'function') return;
    owners.get(doc)?.();
    const form = panel.querySelector('[data-almc-filter-content]');
    const fallbackWasOpen = panel.open;
    const fallbackFocus = doc.activeElement;
    const summary = panel.querySelector('summary');
    const trigger = doc.createElement('button');
    trigger.type = 'button';
    trigger.className = summary.className;
    trigger.innerHTML = summary.innerHTML;
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', 'almc-filter-dialog');
    dialog.id = 'almc-filter-dialog';
    dialog.className = 'almc-filter-dialog';
    dialog.setAttribute('aria-labelledby', 'almc-filter-title');
    dialog.append(form);
    doc.body.append(dialog);
    panel.before(trigger);
    panel.hidden = true;
    panel.dataset.enhanced = 'true';
    form.querySelectorAll('[data-almc-filter-close], [data-almc-filter-clear]').forEach(el => { el.hidden = false; });
    const count = Number(form.querySelector('[data-active-count]')?.dataset.activeCount || 0);
    trigger.querySelector('[data-almc-filter-count]').textContent = count ? `(${count})` : '';

    const apply = form.querySelector('[data-almc-filter-apply]');
    const applyLabel = apply.textContent;
    const clear = form.querySelector('[data-almc-filter-clear]');
    const status = form.querySelector('[data-almc-filter-status]');
    const request = createFilterRequest({fetcher:win.fetch?.bind(win)});
    const supported = typeof win.fetch === 'function' && typeof win.AbortController === 'function';
    let previousOverflow = '';
    let appliedUrl = win.location.href;
    let retryTarget = null;
    let disabled = [];
    let destroyed = false;

    const busy = value => {
        if (value && apply.disabled) return;
        if (value) {
            disabled = [...form.querySelectorAll('input,select')].filter(el => !el.disabled);
            disabled.forEach(el => { el.disabled = true; });
        } else {
            disabled.forEach(el => { el.disabled = false; });
            disabled = [];
        }
        apply.disabled = value;
        clear.disabled = value;
        form.setAttribute('aria-busy', String(value));
    };
    const groups = () => form.querySelectorAll('[data-almc-filter-group]').forEach(group => {
        const n = group.querySelectorAll('input[type="checkbox"]:checked').length;
        group.querySelector('[data-almc-group-count]').textContent = n ? `(${n})` : '';
    });
    const open = () => {
        if (dialog.open) return;
        previousOverflow = doc.body.style.overflow;
        dialog.showModal();
        doc.body.style.overflow = 'hidden';
        trigger.setAttribute('aria-expanded', 'true');
        form.querySelector('[data-almc-filter-close]').focus();
    };
    const close = () => {
        request.cancel();
        busy(false);
        if (retryTarget?.historyMode === 'replace') win.history.replaceState(win.history.state, '', appliedUrl);
        retryTarget = null;
        form.reset();
        form.querySelectorAll('input').forEach(el => el.setCustomValidity(''));
        form.querySelectorAll('[data-almc-filter-option]').forEach(el => { el.hidden = false; });
        form.querySelectorAll('[data-almc-filter-no-match]').forEach(el => { el.hidden = true; });
        status.textContent = '';
        apply.textContent = applyLabel;
        groups();
        if (dialog.open) dialog.close();
    };
    trigger.addEventListener('click', open);
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    dialog.addEventListener('close', () => {
        doc.body.style.overflow = previousOverflow;
        trigger.setAttribute('aria-expanded', 'false');
        if (!destroyed) trigger.focus({preventScroll:true});
    });
    dialog.addEventListener('click', event => {
        if (event.target.closest('[data-almc-filter-close]')) close();
        if (event.target === dialog) {
            const box = dialog.getBoundingClientRect();
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
        }
    });
    form.addEventListener('input', event => {
        retryTarget = null;
        apply.textContent = applyLabel;
        if (event.target.name === 'min' || event.target.name === 'max') form.elements.namedItem('max').setCustomValidity('');
        if (!event.target.matches('[data-almc-filter-search]')) return;
        const group = event.target.closest('[data-almc-filter-group]');
        const normalise = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
        const term = normalise(event.target.value);
        let visible = 0;
        group.querySelectorAll('[data-almc-filter-option]').forEach(option => {
            option.hidden = !normalise(option.textContent).includes(term);
            if (!option.hidden) visible++;
        });
        group.querySelector('[data-almc-filter-no-match]').hidden = visible !== 0;
    });
    form.addEventListener('change', event => {
        if (event.target.matches('[data-almc-filter-family]')) {
            form.querySelectorAll('input[name="sub[]"]').forEach(el => { el.checked = false; });
        }
        groups();
    });
    clear.addEventListener('click', () => {
        if (clear.disabled) return;
        form.querySelectorAll('input:not([type="hidden"]), select').forEach(el => {
            if (el.type === 'checkbox') el.checked = false;
            else el.value = el.name === 'orden' ? 'recientes' : '';
            el.setCustomValidity('');
        });
        form.querySelectorAll('[data-almc-filter-option]').forEach(el => { el.hidden = false; });
        form.querySelectorAll('[data-almc-filter-no-match]').forEach(el => { el.hidden = true; });
        retryTarget = null;
        status.textContent = '';
        apply.textContent = applyLabel;
        groups();
    });

    const navigate = async (url, historyMode = 'push') => {
        if (requiresShopDocument(url)) {
            request.cancel();
            win.location.assign(url.href);
            return;
        }
        open();
        retryTarget = {url, historyMode};
        const data = await request.run(url.href, state => {
            status.textContent = form.dataset[state];
            if (state === 'loading') busy(true);
            if (state === 'error') {
                busy(false);
                apply.textContent = form.dataset.retry;
                if (historyMode === 'replace') win.history.replaceState(win.history.state, '', appliedUrl);
            }
        });
        if (!data || destroyed) return;
        const canonical = new URL(data.canonical, url);
        if (data.hero === true) {
            canonical.hash = url.hash;
            win.location.assign(canonical.href);
            return;
        }
        const fragment = doc.createElement('template');
        fragment.innerHTML = data.html;
        const main = doc.querySelector('[data-almc-shop-content]');
        if (canonical.origin !== win.location.origin || canonical.pathname !== url.pathname || !main || !fragment.content.querySelector('[data-almc-filter-panel]')) {
            status.textContent = form.dataset.error;
            busy(false);
            apply.textContent = form.dataset.retry;
            if (historyMode === 'replace') win.history.replaceState(win.history.state, '', appliedUrl);
            return;
        }
        // Inline scripts are not rerun on a filter update; structured data remains in the DOM.
        fragment.content.querySelectorAll('script:not([type="application/ld+json"])').forEach(el => el.remove());
        canonical.hash = url.hash;
        win.history[historyMode === 'push' ? 'pushState' : 'replaceState']({almcShopFilters:true}, '', canonical.href);
        destroy();
        main.replaceChildren(fragment.content);
        doc.body.toggleAttribute('data-hero-full', data.hero === true);
        if (typeof data.title === 'string') doc.title = data.title;
        const description = doc.querySelector('meta[name="description"]');
        if (description && typeof data.description === 'string') description.content = data.description;
        doc.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach(link => {
            const target = new URL(canonical);
            target.hash = '';
            if (link.hreflang) target.pathname = target.pathname.replace(/^\/(es|en|fr|ca)\/shop/, '/' + (link.hreflang === 'x-default' ? 'es' : link.hreflang) + '/shop');
            link.href = target.href;
        });
        win.shopRevelar?.(main.querySelectorAll('.reveal'));
        initializeShopFilters(doc);
        const result = main.querySelector('[data-almc-filter-result-count]') || main.querySelector('.almc-filter-trigger');
        result?.focus({preventScroll:true});
        result?.scrollIntoView({block:'center', behavior:'instant'});
    };
    form.addEventListener('submit', event => {
        if (!supported) return;
        event.preventDefault();
        if (apply.disabled) return;
        const min = form.elements.namedItem('min');
        const max = form.elements.namedItem('max');
        if (min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
            max.setCustomValidity(form.dataset.invalidPrice);
            max.reportValidity();
            return;
        }
        const target = retryTarget ?? {url:filterUrl(form.action, new win.FormData(form)), historyMode:'push'};
        navigate(target.url, target.historyMode);
    });
    const pagination = () => { appliedUrl = win.location.href; };
    win.addEventListener('almc-shop-pagination', pagination);
    const popstate = () => {
        if (win.location.pathname !== new URL(appliedUrl).pathname) { win.location.reload(); return; }
        navigate(new URL(win.location.href), 'replace');
    };
    const destroy = () => {
        destroyed = true;
        request.cancel();
        if (dialog.open) dialog.close();
        doc.body.style.overflow = previousOverflow;
        dialog.remove();
        win.removeEventListener('almc-shop-pagination', pagination);
        win.removeEventListener('popstate', popstate);
        win.removeEventListener('pagehide', close);
        owners.delete(doc);
    };
    if (supported) win.addEventListener('popstate', popstate);
    win.addEventListener('pagehide', close);
    owners.set(doc, destroy);
    if (fallbackWasOpen) {
        open();
        if (form.contains(fallbackFocus)) fallbackFocus.focus();
    }
    return {open, close, destroy};
}
if (typeof document !== 'undefined') initializeShopFilters();
